import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastType } from './ui-enum';
import { Observable, Subject } from 'rxjs';

interface ToastOption{
  type?: ToastType,
  duration?: number,
  translateParams?: any,
}

export enum UIActionType{
  HideBackButton,
  ShowBackButton,
  ShowBookingSearchDialog,
}

export interface UIActionEvent{
  type: UIActionType,
  data?: any
}

export const TOAST_FOREVER = 999999;

@Injectable({
  providedIn: 'root'
})
export class UiService {

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private confirmService: ConfirmationService
  ) { }

  toastSuccess(msg: string, opts?: ToastOption){
    this.toast(msg, {type: 'success', ...opts});
  }

  toastSuccessT(msg: string, opts?: ToastOption){
    this.toastT(msg, {type: 'success', ...opts});
  }

  toastErrorT(msg: string, opts?: ToastOption){
    this.toastT(msg, {type: 'error', ...opts});
  }

  toastError(msg: string, opts?: ToastOption){
    this.toast(msg, {type: 'error', ...opts});
  }

  toast(msg: string, opts?: ToastOption){
    const severity = opts?.type || 'success';
    const summary = this.translate.instant(`message-service-summary-${severity}`); 

    this.messageService.add({severity: severity, summary: summary, detail: msg, key: 'toast'});
  }

  toastT(msgKey: string, opts?: ToastOption){
    const value = this.translate.instant(msgKey, opts?.translateParams);
    if(value){
      return this.toast(value, opts);
    }

    this.translate.get(msgKey, opts?.translateParams).subscribe(text => {
      this.toast(text, opts);
    })
  }

  alert({title, message, acceptLabel, type, showCancelButton, cancelLabel, key: dialogKey}: UIAlertParams, done?: UIAlertListener){
    if(!message) return;
    const trans = [title || 'common.alert', message, acceptLabel || 'common.ok', cancelLabel || 'common.no'];
    this.translate.get([title || 'common.alert', message, acceptLabel || 'common.ok']).subscribe(texts => {
      let key = dialogKey || 'global';

      if(!dialogKey){
        if(type === 'success'){
          key = 'global-success';
        }
        else if(type === 'error'){
          key = 'global-error';
        }
      }
    
      this.confirmService.confirm({
        header: texts[trans[0]],
        message: texts[trans[1]],
        acceptLabel: texts[trans[2]],
        rejectVisible: showCancelButton,
        rejectLabel: texts[trans[3]],
        key: key,
        accept: () => {
          done?.(true);
        },
        reject: () => {
          done?.(false);
        }
      })
    })
  }

  confirm(params: UIAlertParams, done: UIAlertListener){
    params.showCancelButton = true;
    params.title = 'common.confirm';
    params.acceptLabel = 'common.yes';
    params.cancelLabel = 'common.no';

    return this.alert(params, done);
  }

  actionsSubject = new Subject<UIActionEvent>();
  backSubject = new Subject<any>();
  stepperHeaderSubjet = new Subject<{
    steps: any,
    currentStep: number
  } | null | undefined>();
  private stepperHeaderClick = new Subject<number>();
  
  hideBackButton(){
    this.actionsSubject.next({
      type: UIActionType.HideBackButton
    })
  }

  showBackButton(){
    this.actionsSubject.next({
      type: UIActionType.ShowBackButton
    })
  }

  goBack(){
    this.backSubject.next(null);
  }

  back(): Observable<any>{
    return new Observable((observable) => {
      const sub = this.backSubject.subscribe((event) => {
        observable.next(event);
      })

      return {
        unsubscribe: () => {
          sub.unsubscribe();
        }
      }
    })
  }

  getHeaderStepper(): Subject<any>{
    return this.stepperHeaderSubjet;
  }

  showStepperOnHeader(steps: any, currentStep: number){
    this.stepperHeaderSubjet.next({
      steps: steps,
      currentStep: currentStep
    });
  }
  
  hideStepperOnHeader(){
    this.stepperHeaderSubjet.next(null);
  }

  onStepperHeaderClick(){
    return this.stepperHeaderClick;
  }

  emitStepperHeaderClick(index: number){
    this.stepperHeaderClick.next(index);
  }
}

export interface UIAlertParams{
  title?: string, 
  message: string, 
  acceptLabel?: string, 
  cancelLabel?: string,
  type?: 'info' | 'success' | 'error',
  showCancelButton?: boolean,
  key?: string
}

export type UIAlertListener = (status: boolean) => void